import React from "react";

import {
  faCheck,
  faExclamationCircle,
  faQuestionCircle,
  faTimes,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";

import styled from "styled-components/macro";

import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import { Tooltip } from "components/Form/FormikControls/Tooltip";

import { formatUTCToLocalDateTimeString } from "lib/timeFormats";

import AgriNousActiveLogo from "img/AgriNousActiveLogo";

export function booleanTickRenderer(props) {
  const { value } = props;
  if (value === true) {
    return <FaIcon icon={faCheck} color="success" />;
  }
  return <FaIcon icon={faExclamationCircle} color="warning" />;
}

export function nullableTickRenderer(props) {
  const {
    data,
    value,
    tooltipComponent: TooltipComponent,
    buildToolTipProps,
    showWarningOnFalse = true,
  } = props;
  let FaIconComponent = null;

  if (typeof value === "boolean") {
    if (value === true) {
      FaIconComponent = <FaIcon icon={faCheck} color="success" />;
    }
    if (value === false) {
      FaIconComponent = (
        <FaIcon
          icon={showWarningOnFalse ? faExclamationCircle : faTimes}
          color={showWarningOnFalse ? "warning" : "error"}
        />
      );
    }
  } else {
    FaIconComponent = <FaIcon icon={faQuestionCircle} color="primary" />;
  }

  if (TooltipComponent && buildToolTipProps) {
    return (
      <Tooltip title={<TooltipComponent {...buildToolTipProps(data)} />}>
        {FaIconComponent}
      </Tooltip>
    );
  }
  return FaIconComponent;
}

export function nullableBooleanRenderer(props) {
  const { value, colDef } = props;
  if (typeof value === "boolean") {
    if (value === true) {
      return <FaIcon icon={faCheck} color="success" />;
    }
    return <FaIcon icon={faTimes} color="error" />;
  }

  if (colDef.showRowGroup) {
    return value;
  }

  return null;
}

interface BooleanIconProps {
  value: string | boolean;
}

const BooleanIcon = ({ value }: BooleanIconProps) => {
  if (value === true || value === "true") {
    return <FaIcon icon={faCheck} color="success" />;
  }
  if (value === false || value === "false") {
    return <FaIcon icon={faTimes} color="error" />;
  }
  return "";
};

export function booleanRenderer(props) {
  const {
    data,
    value,
    tooltipComponent: TooltipComponent,
    buildToolTipProps,
  } = props;
  // 'true' and 'false' come from grouped columns!

  if (TooltipComponent) {
    return (
      <Tooltip title={<TooltipComponent {...buildToolTipProps(data)} />}>
        <BooleanIcon value={value} />
      </Tooltip>
    );
  }
  return <BooleanIcon value={value} />;
}

export function timeValueExistsRenderer(props) {
  // We have a value, and the value is good.
  const { value } = props;
  if (value !== null && value !== undefined) {
    return (
      <FaIcon
        icon={faCheck}
        color="success"
        title={formatUTCToLocalDateTimeString(value)}
      />
    );
  }
  return null;
}

export function invertedTimeValueExistsRenderer(props) {
  // We have a value, and the value is bad.
  const { value } = props;
  if (value !== null && value !== undefined) {
    return (
      <FaIcon
        icon={faTimes}
        color="error"
        title={formatUTCToLocalDateTimeString(value)}
      />
    );
  }
  return null;
}

export function valueExistsRenderer(props) {
  // We have a value, and the value is good.
  const { value } = props;
  if (value !== null && value !== undefined) {
    return (
      <FaIcon
        icon={faCheck}
        color="success"
        title={typeof value === "string" ? value : undefined}
      />
    );
  }
  return null;
}

const IconWrapper = styled.div`
  ${({ theme }) => `
  margin: auto ${theme.space[1]}px;
  padding-top: 3px;
`}
`;
interface AgriNousManagedRendererProps {
  value: {
    isManaged: boolean;
    nonManagedIcon: IconDefinition;
  };
}
export function AgriNousManagedRenderer({
  value,
}: AgriNousManagedRendererProps) {
  const { isManaged, nonManagedIcon } = value;
  return isManaged || !nonManagedIcon ? (
    <AgriNousActiveLogo isActive={isManaged} scale={0.4} size={20} />
  ) : (
    <IconWrapper>
      <FaIcon icon={nonManagedIcon} />
    </IconWrapper>
  );
}
